import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { findProduct, productPath, productSlug, useSeo, categoryPath, breadcrumbLd, absUrl, clip } from "@/lib/seo";
import { ShoppingBasket, Check } from "lucide-react";
import { CONDITIONS, gbp } from "@/data/shop";
import { useProducts } from "@/lib/products";
import { api, srcSet } from "@/lib/api";
import { ConditionBadge } from "@/components/shop/ProductCard";
import { Breadcrumbs } from "@/components/shop/Breadcrumbs";
import NotFound from "@/pages/NotFound";

const CONDITION_LD = { New: "NewCondition", "For Parts / Not Working": "DamagedCondition" }; // Open Box and used grades -> UsedCondition

const productLd = (p, url, delivery) => ({
  "@type": "Product",
  name: p.name,
  description: clip(p.desc || `${p.name}, ${p.condition}.`, 4000),
  image: p.images.map((i) => absUrl(i)),
  ...(p.sku && { sku: p.sku }),
  ...(p.brand && { brand: { "@type": "Brand", name: p.brand } }),
  category: p.category,
  offers: {
    "@type": "Offer",
    url,
    price: p.price.toFixed(2),
    priceCurrency: "GBP",
    availability: `https://schema.org/${p.qty > 0 ? "InStock" : "OutOfStock"}`,
    itemCondition: `https://schema.org/${CONDITION_LD[p.condition] || "UsedCondition"}`,
    seller: { "@type": "Organization", name: "ShopaBee" },
    ...(delivery != null && { shippingDetails: {
      "@type": "OfferShippingDetails",
      shippingRate: { "@type": "MonetaryAmount", value: Math.max(delivery, p.deliveryCharge || 0).toFixed(2), currency: "GBP" },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "GB" },
    } }),
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy", applicableCountry: "GB", returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: 14, returnMethod: "https://schema.org/ReturnByMail",
    },
  },
});

const Info = ({ title, testId, children }) => (
  <div className="py-4 border-t border-[#202A35]/10" data-testid={testId}>
    <h2 className="font-display font-semibold text-[#202A35] mb-1.5">{title}</h2>
    <div className="text-sm text-[#202A35]/75 leading-relaxed">{children}</div>
  </div>
);

const Gallery = ({ images, name }) => {
  const [active, setActive] = useState(0);
  return (
    <div data-testid="product-gallery">
      <div className="aspect-square rounded-3xl overflow-hidden bg-[#F5F6F7] border-2 border-[#202A35]/10">
        <img src={images[active]} srcSet={srcSet(images[active])} sizes="(min-width: 1024px) 600px, 100vw" width="800" height="800" fetchpriority="high" alt={name} className="w-full h-full object-cover" data-testid="gallery-main-image" />
      </div>
      <div className="mt-3 flex gap-2">
        {images.map((src, i) => (
          <button key={src} onClick={() => setActive(i)} aria-label={`View image ${i + 1}`} data-testid={`gallery-thumb-${i}`}
            className={`w-16 h-16 rounded-xl overflow-hidden border-2 ${i === active ? "border-[#E2A100]" : "border-transparent"}`}>
            <img src={srcSet(src) ? `${src}?w=400` : src} alt="" width="64" height="64" loading="lazy" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default function ProductPage({ onAdd }) {
  const { id } = useParams();
  const { products, loading } = useProducts();
  const navigate = useNavigate();
  const p = findProduct(products, id);
  const [delivery, setDelivery] = useState(null);
  useEffect(() => { api.get("/delivery").then((r) => setDelivery(r.data.price)).catch(() => {}); }, []);
  const path = p ? productPath(p) : undefined;
  const crumbs = p ? [{ name: "Home", path: "/" }, { name: p.category, path: categoryPath(p.categoryId) }, { name: p.name, path }] : [];
  useSeo(p ? {
    title: clip(p.name, 48), path, image: p.images[0], type: "product",
    description: p.desc ? clip(`${p.desc} ${p.condition}, ${gbp(p.price)} at ShopaBee.`) : `${p.name} in ${p.condition} condition for ${gbp(p.price)}. Condition, contents and any defects clearly described. UK delivery from ShopaBee.`,
    noindex: !!p.demo,
    jsonLd: { "@context": "https://schema.org", "@graph": [productLd(p, absUrl(path), delivery), breadcrumbLd(crumbs)] },
  } : { title: loading ? "" : "Page not found", noindex: true });
  useEffect(() => {
    if (p && id !== productSlug(p)) navigate(productPath(p), { replace: true }); // old id links move to the readable address
  }, [p, id, navigate]);
  const pid = p?.id;
  useEffect(() => {
    if (!pid) return;
    // Cookie-free: count each product once per tab session, never on the admin's own device.
    try {
      if (localStorage.getItem("sb-staff")) return;
      const seen = JSON.parse(sessionStorage.getItem("sb-pv") || "[]");
      if (seen.includes(pid)) return;
      sessionStorage.setItem("sb-pv", JSON.stringify([...seen, pid]));
    } catch { return; }
    api.post("/product-views", { product_id: pid }).catch(() => {});
  }, [pid]);
  if (!p && loading) return <section className="max-w-7xl mx-auto px-4 py-20 text-center text-[#202A35]/60" data-testid="product-loading">Loading…</section>;

  if (!p) return <NotFound testId="product-not-found" title="Hmm, we couldn't find that one." />;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="product-page">
      <Breadcrumbs items={crumbs} />
      <div className="mt-5 grid lg:grid-cols-2 gap-8 lg:gap-12">
        <Gallery images={p.images} name={p.name} />
        <div>
          <Link to={categoryPath(p.categoryId)} className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B87F00] hover:underline" data-testid="product-category-link">{p.category}</Link>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#202A35]" data-testid="product-title">{p.name}</h1>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <span className="font-display text-3xl font-bold text-[#202A35]" data-testid="product-price">{gbp(p.price)}</span>
            {p.was && <span className="text-sm text-[#202A35]/55">was <span className="line-through">{gbp(p.was)}</span></span>}
            <ConditionBadge condition={p.condition} testId="product-condition" className="text-xs px-3 py-1" />
          </div>
          <p className="mt-3 text-sm font-semibold text-[#202A35]" data-testid="product-quantity">
            {p.qty === 0 ? "Out of Stock" : p.qty === 1 ? "Only one left!" : `${p.qty} available`}
          </p>
          <button onClick={() => onAdd(p)} disabled={p.qty === 0} data-testid="product-add-to-basket"
            className="mt-5 h-12 px-8 rounded-full bg-[#FFD23F] text-[#202A35] font-bold inline-flex items-center gap-2 hover:bg-[#202A35] hover:text-white transition-colors disabled:bg-[#F5F6F7] disabled:text-[#202A35]/45 disabled:cursor-not-allowed">
            <ShoppingBasket size={18} /> {p.qty === 0 ? "Out of Stock" : "Add to Basket"}
          </button>
          <div className="mt-6">
            <Info title={`Condition: ${p.condition}`} testId="product-condition-description">
              {CONDITIONS[p.condition]}
              {p.conditionNotes && <p className="mt-1.5 text-[#202A35]" data-testid="product-condition-notes">{p.conditionNotes}</p>}
            </Info>
            {p.brand && <Info title="Brand" testId="product-brand">{p.brand}</Info>}
            <Info title="Description" testId="product-description">{p.desc || "No description provided."}</Info>
            <Info title="What's included" testId="product-included">
              {p.included.length ? <ul className="space-y-1">{p.included.map((i) => <li key={i} className="flex gap-2"><Check size={16} className="text-[#2BB673] mt-0.5" />{i}</li>)}</ul> : "Not specified."}
            </Info>
            <Info title="Known defects or imperfections" testId="product-defects">{p.defects || "None noted for this item."}</Info>
            <p className="pt-4 border-t border-[#202A35]/10 text-xs text-[#202A35]/55" data-testid="product-seller">Sold by {p.seller.name}{p.sku && <> · SKU <span data-testid="product-sku">{p.sku}</span></>}{p.demo && " · Demo item for preview only, not real stock."}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
